/**
 * 文字の幅と折り返し（#217、新しい描き手）。DOM も Node も使わない＝エディタ（ブラウザ）と PDF（Chromium）で同じ所で折れる。
 *   ・幅は字の種類ごとの見積もり（全角＝1em、半角の英数字はやや広めに見積もる＝実際の字より狭く見積もって欄からはみ出すことが無いように）
 *   ・折る位置は自分で決めて、描くときは <br> でつなぐ（ブラウザに折らせない）＝行の数・行の高さ・ページの割り付けがどの環境でも同じ
 *   ・禁則：閉じ括弧・句読点・小さい仮名・長音は行の頭に置かない／開き括弧は行の終わりに置かない（前の字ごと次の行へ送る）
 *   ・英数字のつながり（"T1234567890123"・"1,800"）は途中で折らない（欄より長いときだけ字の途中で折る）
 */

/** 1pt の mm */
export const PT = 0.3528;

const NO_START = new Set(Array.from("、。，．,.・：；:;？！?!‼⁇⁈⁉）」』】〕｝〉》〙〗)]}ゝゞヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎ々〻‐゠–〜～%’”"));
const NO_END = new Set(Array.from("（「『【〔｛〈《〘〖([{‘“"));

const NARROW = new Set(Array.from("iljI.,:;'|!`"));
const SEMI = new Set(Array.from("ftr()[]{}-/\\\"*"));
const WIDE = new Set(Array.from("mwMW@%"));

export type WidthFont = "serif" | "sans" | "maru";

/**
 * 1文字の幅（em）。PDF を刷るサーバーの書体（Noto Sans／Serif CJK JP）に合わせ、少しだけ広めに見積もる
 * （2026-10-19 Chromium で測った Noto：数字 0.555em・明朝の数字 0.567em・大文字 0.632em・小文字 0.526em・「,」0.278em）。
 * Mac のヒラギノは半角がもっと広い（数字 0.66em）＝エディタでは英数字が少し詰まって見えることがある（数字の欄は切らずに見せる）
 */
export function charEm(c: string, font: WidthFont = "sans"): number {
  const cp = c.codePointAt(0) ?? 0;
  if (cp < 0x80) {
    if (c === " ") return 0.25;
    if (cp < 0x20) return 0;
    if (c >= "0" && c <= "9") return font === "serif" ? 0.59 : 0.58;
    if (NARROW.has(c)) return 0.3;
    if (SEMI.has(c)) return 0.37;
    if (WIDE.has(c)) return 0.86;
    if (c >= "A" && c <= "Z") return 0.66;
    if (c >= "a" && c <= "z") return 0.55;
    return 0.58;
  }
  // 半角カナ
  if (cp >= 0xff61 && cp <= 0xff9f) return 0.52;
  // 欧文の記号・ラテン（全角でないもの）
  if (cp < 0x1100) return 0.62;
  // 全角（漢字・仮名・全角記号・CJK の互換文字 ㎡ など）
  return 1;
}

/** 文字列の幅（mm）。ls は字間（em） */
export function textWidthMm(s: string, sizePt: number, font: WidthFont = "sans", ls = 0): number {
  let em = 0;
  for (const c of s) em += charEm(c, font) + ls;
  return em * sizePt * PT;
}

const isWordChar = (c: string): boolean => /[0-9A-Za-z_,.\-+#&'@%/:]/.test(c);

/** i の字の前で折ってよいか */
function canBreakBefore(chars: string[], i: number): boolean {
  const prev = chars[i - 1]!;
  const cur = chars[i]!;
  if (cur === " ") return false;
  if (NO_START.has(cur)) return false;
  if (NO_END.has(prev)) return false;
  if (prev === " ") return true;
  if (isWordChar(prev) && isWordChar(cur)) return false;
  return true;
}

/**
 * 文字列を幅（mm）で折る。改行（\n）はそのまま行を分ける。空の文字列は [""]（1行）。
 * 幅が字1つより狭くても、1行に少なくとも1字は入れる（止まらないように）
 */
export function wrapText(text: string, widthMm: number, sizePt: number, font: WidthFont = "sans"): string[] {
  const out: string[] = [];
  for (const para of text.replace(/\r\n?/g, "\n").split("\n")) out.push(...wrapPara(para, widthMm, sizePt, font));
  return out.length ? out : [""];
}

function wrapPara(para: string, widthMm: number, sizePt: number, font: WidthFont): string[] {
  const chars = Array.from(para);
  if (!chars.length) return [""];
  const em = sizePt * PT;
  const maxEm = widthMm / em;
  if (!(maxEm > 0)) return [para];
  const lines: string[] = [];
  let start = 0;
  while (start < chars.length) {
    let w = 0;
    let i = start;
    let lastBreak = -1;
    while (i < chars.length) {
      const cw = charEm(chars[i]!, font);
      if (i > start && canBreakBefore(chars, i)) lastBreak = i;
      if (w + cw > maxEm + 1e-6 && i > start) break;
      w += cw;
      i++;
    }
    if (i >= chars.length) {
      lines.push(chars.slice(start).join(""));
      break;
    }
    // i の字が入らない：折ってよい最後の位置で折る（無ければ i で折る＝長い英数字のつながり）
    let b = lastBreak > start ? lastBreak : i;
    // 行の頭に置けない字が i に来ていて、折る位置が無いときは、前の字ごと送る（入りきらない1字だけ行からはみ出させない）
    if (b === i && NO_START.has(chars[i]!) && i - 1 > start) b = i - 1;
    lines.push(chars.slice(start, b).join("").replace(/ +$/, ""));
    start = b;
    while (start < chars.length && chars[start] === " ") start++;
  }
  return lines;
}

/** 1行に収める文字の大きさ（pt）。入りきれば size のまま、入らなければ縮める（min まで） */
export function fitSize(text: string, widthMm: number, sizePt: number, min = 5, font: WidthFont = "sans"): number {
  const w = textWidthMm(text, sizePt, font);
  if (w <= widthMm || w <= 0) return sizePt;
  return Math.max(min, Math.floor(((sizePt * widthMm) / w) * 10) / 10);
}
